import api from './api';

export interface OfferStandalone {
  id: string;
  offer_id?: string;
  offer_sequence_number?: number;
  offer_preparer?: string;
  offer_preparer_avatar_url?: string;
  offer_preparer_name?: string;
  client_name: string;
  client_address: string;
  order_name: string;
  location?: string;
  investment_address: string;
  site_inspection: 'Tak' | 'Nie';
  site_inspection_date?: string;
  submission_deadline: string;
  estimated_order_value?: number;
  bid_bond?: number;
  status: 'w trakcie' | 'Prekwalifikacja' | 'najlepsza oferta' | 'druga oferta' | 'trzecia i dalej oferta' | 'Uzupełnianie dokumentów' | 'Wygrana' | 'odrzucona' | 'przegrana';
  client_website?: string;
  google_drive_link?: string;
  miro_link?: string;
  technical_experience?: string;
  personnel_potential?: string;
  economic_situation?: string;
  client_forms_path?: string;
  specification_path?: string;
  created_by_user_id?: string;
  created_at?: string;
  updated_at?: string;
  /** ID boardu w Collectifile – gdy ustawione, oferta ma board (link pod ikoną) */
  collectifile_board_id?: string | null;
}

export interface CreateOfferStandaloneRequest {
  // Przechowujemy user_id osoby przygotowującej
  offer_preparer?: string;
  client_name: string;
  client_address: string;
  order_name: string;
  location?: string;
  investment_address: string;
  site_inspection: 'Tak' | 'Nie';
  site_inspection_date?: string;
  submission_deadline: string;
  estimated_order_value?: number;
  bid_bond?: number;
  status?: 'w trakcie' | 'Prekwalifikacja' | 'najlepsza oferta' | 'druga oferta' | 'trzecia i dalej oferta' | 'Uzupełnianie dokumentów' | 'Wygrana' | 'odrzucona' | 'przegrana';
  client_website?: string;
  miro_link?: string;
  technical_experience?: string;
  personnel_potential?: string;
  economic_situation?: string;
}

export interface UpdateOfferStandaloneRequest extends Partial<CreateOfferStandaloneRequest> {
  id: string;
}

export interface FileUploadResponse {
  message: string;
  results: Array<{
    fileId?: string;
    fileName: string;
    subfolder?: string;
    error?: string;
  }>;
}

export interface GenerateFolderResponse {
  google_drive_link: string;
  message: string;
}

/** Segment URL `/app/easy-estimate/offer/:segment` — preferuj `offer_id` (np. OF.396.2026), inaczej UUID. */
export function getStandaloneOfferUrlSegment(offer: OfferStandalone): string {
  const oid = offer.offer_id?.trim();
  if (oid) return oid;
  if (offer.offer_sequence_number != null && offer.created_at) {
    return `OF.${offer.offer_sequence_number}.${new Date(offer.created_at).getFullYear()}`;
  }
  return offer.id;
}

class OfferStandaloneService {
  private baseURL = '/offers-standalone';

  async getAllOffers(): Promise<OfferStandalone[]> {
    try {
      const response = await api.get(this.baseURL);
      return response.data;
    } catch (error) {
      console.error('Error fetching offers standalone:', error);
      throw error;
    }
  }

  async getOfferById(id: string): Promise<OfferStandalone> {
    try {
      const response = await api.get(`${this.baseURL}/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching offer standalone:', error);
      throw error;
    }
  }

  async createOffer(offerData: CreateOfferStandaloneRequest): Promise<OfferStandalone> {
    try {
      const response = await api.post(this.baseURL, offerData);
      return response.data;
    } catch (error) {
      console.error('Error creating offer standalone:', error);
      throw error;
    }
  }

  async updateOffer(id: string, offerData: Partial<CreateOfferStandaloneRequest>): Promise<OfferStandalone> {
    try {
      const response = await api.put(`${this.baseURL}/${id}`, offerData);
      return response.data;
    } catch (error) {
      console.error('Error updating offer standalone:', error);
      throw error;
    }
  }

  async deleteOffer(id: string): Promise<void> {
    try {
      await api.delete(`${this.baseURL}/${id}`);
    } catch (error) {
      console.error('Error deleting offer standalone:', error);
      throw error;
    }
  }

  async generateDriveFolder(id: string): Promise<GenerateFolderResponse> {
    try {
      const response = await api.post(`${this.baseURL}/${id}/generate-folder`);
      return response.data;
    } catch (error) {
      console.error('Error generating Google Drive folder:', error);
      throw error;
    }
  }

  async uploadClientForms(id: string, files: File[]): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post(`${this.baseURL}/${id}/upload-client-forms`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading client forms:', error);
      throw error;
    }
  }

  async uploadSpecification(id: string, files: File[]): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post(`${this.baseURL}/${id}/upload-specification`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading specification:', error);
      throw error;
    }
  }

  async validateImport(payload: any[] | any): Promise<{ success: boolean; total: number; valid: number; invalid: number; report: any[] }>{
    const response = await api.post(`/offers-standalone/validate-import`, payload, { headers: { 'Content-Type': 'application/json' } });
    return response.data;
  }

  // Pomocnicze metody
  getStatusOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'w trakcie', label: 'W trakcie' },
      { value: 'Prekwalifikacja', label: 'Prekwalifikacja' },
      { value: 'najlepsza oferta', label: 'Najlepsza oferta' },
      { value: 'druga oferta', label: 'Druga oferta' },
      { value: 'trzecia i dalej oferta', label: 'Trzecia i dalej oferta' },
      { value: 'Uzupełnianie dokumentów', label: 'Uzupełnianie dokumentów' },
      { value: 'Wygrana', label: 'Wygrana' },
      { value: 'odrzucona', label: 'Odrzucona' },
      { value: 'przegrana', label: 'Przegrana' }
    ];
  }

  getSiteInspectionOptions(): Array<{ value: 'Tak' | 'Nie'; label: string }> {
    return [
      { value: 'Nie', label: 'Nie' },
      { value: 'Tak', label: 'Tak' }
    ];
  }

  formatCurrency(amount?: number): string {
    if (!amount) return '';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pl-PL');
  }

  formatDateTime(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('pl-PL');
  }
}

export const offerStandaloneService = new OfferStandaloneService();
